import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation } from '@tanstack/react-router'

export interface ChatMessage {
  role: 'user' | 'assistant'
  content: string
}

interface ChatContextValue {
  messages: ChatMessage[]
  send: (content: string) => void
  isStreaming: boolean
  error: string | null
  clearMessages: () => void
  isOpen: boolean
  setIsOpen: (open: boolean) => void
}

const ChatContext = createContext<ChatContextValue | null>(null)

const STORAGE_KEY = 'ht-chat-messages'
const SESSION_KEY = 'ht-chat-session'

function getSessionId(): string {
  if (typeof window === 'undefined') return ''
  let id = sessionStorage.getItem(SESSION_KEY)
  if (!id) {
    id = crypto.randomUUID()
    sessionStorage.setItem(SESSION_KEY, id)
  }
  return id
}

function loadMessages(): ChatMessage[] {
  if (typeof window === 'undefined') return []
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as ChatMessage[]) : []
  } catch {
    return []
  }
}

function saveMessages(messages: ChatMessage[]) {
  if (typeof window === 'undefined') return
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(messages))
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const { i18n } = useTranslation()
  const location = useLocation()
  const [messages, setMessages] = useState<ChatMessage[]>(loadMessages)
  const [isStreaming, setIsStreaming] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    saveMessages(messages)
  }, [messages])

  const send = useCallback(async (content: string) => {
    if (isStreaming || !content.trim()) return

    const userMessage: ChatMessage = { role: 'user', content: content.trim() }
    const updatedMessages = [...messages, userMessage]
    setMessages(updatedMessages)
    setIsStreaming(true)
    setError(null)

    const withAssistant = [...updatedMessages, { role: 'assistant' as const, content: '' }]
    setMessages(withAssistant)

    const controller = new AbortController()
    abortRef.current = controller

    try {
      const apiUrl = import.meta.env.VITE_API_URL || '/api'
      const response = await fetch(`${apiUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: updatedMessages.map(m => ({ role: m.role, content: m.content })),
          sessionId: getSessionId(),
          locale: i18n.language,
          pageContext: `${location.pathname}${document.title ? ' — ' + document.title : ''}`,
        }),
        signal: controller.signal,
      })

      if (!response.ok) {
        const text = await response.text()
        throw new Error(text || `HTTP ${response.status}`)
      }

      const reader = response.body?.getReader()
      if (!reader) throw new Error('No response stream')

      const decoder = new TextDecoder()
      let buffer = ''
      let assistantContent = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const data = line.slice(6)
          if (data === '[DONE]') break

          try {
            const parsed = JSON.parse(data) as { content: string; done: boolean }
            if (parsed.content) {
              assistantContent += parsed.content
              setMessages(prev => {
                const updated = [...prev]
                updated[updated.length - 1] = { role: 'assistant', content: assistantContent }
                return updated
              })
            }
          } catch { /* skip malformed */ }
        }
      }
    } catch (err) {
      if ((err as Error).name !== 'AbortError') {
        setError((err as Error).message)
        setMessages(prev => prev.filter(m => m.content !== '' || m.role !== 'assistant'))
      }
    } finally {
      setIsStreaming(false)
      abortRef.current = null
    }
  }, [messages, isStreaming])

  const clearMessages = useCallback(() => {
    setMessages([])
    if (typeof window !== 'undefined') {
      sessionStorage.removeItem(STORAGE_KEY)
      sessionStorage.removeItem(SESSION_KEY)
    }
  }, [])

  return (
    <ChatContext.Provider value={{ messages, send, isStreaming, error, clearMessages, isOpen, setIsOpen }}>
      {children}
    </ChatContext.Provider>
  )
}

const NOOP_CHAT: ChatContextValue = {
  messages: [],
  send: () => {},
  isStreaming: false,
  error: null,
  clearMessages: () => {},
  isOpen: false,
  setIsOpen: () => {},
}

export function useChat() {
  const ctx = useContext(ChatContext)
  return ctx ?? NOOP_CHAT
}
